import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Generator } from './useGenerators';
import type { GeneratorConfigV1 } from '../types/generator-config';
import { isGeneratorConfigV1 } from '../types/generator-config';
import { createDefaultConfig } from '../lib/defaultConfig';

interface UseGeneratorConfigResult {
    generator: Generator | null;
    config: GeneratorConfigV1 | null;
    loading: boolean;
    error: string | null;
    saving: boolean;
    publishing: boolean;
    isDirty: boolean;
    /** Mutation locale (ne persiste pas encore) */
    updateConfig: (updater: (prev: GeneratorConfigV1) => GeneratorConfigV1) => void;
    /** Persiste la config courante dans generators.config */
    save: () => Promise<void>;
    /** Publie / dépublie le générateur. Si isDirty, save d'abord. */
    setPublished: (next: boolean) => Promise<void>;
    /** Recharge depuis la DB (discard les changements locaux) */
    reload: () => Promise<void>;
}

export function useGeneratorConfig(generatorId: string | undefined): UseGeneratorConfigResult {
    const [generator, setGenerator] = useState<Generator | null>(null);
    const [config, setConfig] = useState<GeneratorConfigV1 | null>(null);
    const [savedConfig, setSavedConfig] = useState<GeneratorConfigV1 | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!generatorId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);

        const { data, error: err } = await supabase
            .from('generators')
            .select('*')
            .eq('id', generatorId)
            .maybeSingle();

        if (err) {
            setError(err.message);
            setLoading(false);
            return;
        }
        if (!data) {
            setError('Générateur introuvable.');
            setLoading(false);
            return;
        }

        const gen = data as Generator;
        setGenerator(gen);

        // Hydrate la config : si elle est vide ou pas en v1, on génère un défaut
        const raw = gen.config as unknown;
        const hydrated: GeneratorConfigV1 = isGeneratorConfigV1(raw)
            ? raw
            : createDefaultConfig({
                  companyName: gen.name,
                  industry: gen.industry,
              });

        setConfig(hydrated);
        setSavedConfig(hydrated);
        setLoading(false);
    }, [generatorId]);

    useEffect(() => {
        void load();
    }, [load]);

    const updateConfig = useCallback(
        (updater: (prev: GeneratorConfigV1) => GeneratorConfigV1) => {
            setConfig((prev) => (prev ? updater(prev) : prev));
        },
        []
    );

    const save = useCallback(async () => {
        if (!generator || !config) return;
        setSaving(true);
        setError(null);

        const { data, error: err } = await supabase
            .from('generators')
            .update({ config: config as unknown as Record<string, unknown> })
            .eq('id', generator.id)
            .select('*')
            .single();

        if (err) {
            setError(err.message);
            setSaving(false);
            return;
        }

        setGenerator(data as Generator);
        setSavedConfig(config);
        setSaving(false);
    }, [generator, config]);

    const isDirty = !!(config && savedConfig && JSON.stringify(config) !== JSON.stringify(savedConfig));

    const setPublished = useCallback(
        async (next: boolean) => {
            if (!generator || !config) return;
            setPublishing(true);
            setError(null);

            // Si des modifs locales non sauvegardées, on les persiste avant de publier
            // (on veut que la page publique reflète la config à jour).
            const configToSave = isDirty ? config : null;

            const update: Record<string, unknown> = { is_published: next };
            if (configToSave) {
                update.config = configToSave as unknown as Record<string, unknown>;
            }

            const { data, error: err } = await supabase
                .from('generators')
                .update(update)
                .eq('id', generator.id)
                .select('*')
                .single();

            if (err) {
                setError(err.message);
                setPublishing(false);
                return;
            }

            setGenerator(data as Generator);
            if (configToSave) setSavedConfig(configToSave);
            setPublishing(false);
        },
        [generator, config, isDirty]
    );

    return {
        generator,
        config,
        loading,
        error,
        saving,
        publishing,
        isDirty,
        updateConfig,
        save,
        setPublished,
        reload: load,
    };
}
